import React, { useState } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import useTruckNotes from '../hooks/useTruckNotes';
import './TruckNotesThread.css';

const NOTE_TYPE_LABEL = {
  scheduler: 'Scheduler',
  receiver: 'Receiver',
  shipper: 'Shipper',
  general: 'General',
};

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

function userIsAdmin(currentUser) {
  if (!currentUser || !Array.isArray(currentUser.roles)) return false;
  return (
    currentUser.roles.includes(1) ||
    currentUser.roles.some((r) => r && typeof r === 'object' && r.id === 1)
  );
}

/**
 * Thread of notes attached to a single truck (inbound or outbound).
 *
 * @param {Object} props
 * @param {'inbound'|'outbound'} props.truckType
 * @param {number|string} props.truckId
 * @param {'edit'|'read'} [props.mode] default 'edit'
 * @param {'light'|'dark'} [props.theme] default 'light'
 * @param {string} [props.emptyMessage]
 * @param {string} [props.className]
 */
export default function TruckNotesThread({
  truckType,
  truckId,
  mode = 'edit',
  theme = 'light',
  emptyMessage,
  className = '',
}) {
  const { currentUser } = useAuth();
  const { notes, loading, error, addNote, editNote, removeNote } = useTruckNotes({
    truckType,
    truckId,
  });

  const [composer, setComposer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [actionError, setActionError] = useState(null);

  const isAdmin = userIsAdmin(currentUser);
  const canEdit = mode === 'edit' && !!currentUser?.id;

  const handleAdd = async (e) => {
    e.preventDefault();
    const trimmed = composer.trim();
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await addNote(trimmed);
      setComposer('');
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditDraft(note.content);
    setActionError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft('');
  };

  const saveEdit = async (note) => {
    const trimmed = editDraft.trim();
    if (!trimmed || trimmed === note.content) {
      cancelEdit();
      return;
    }
    try {
      await editNote(note.id, trimmed);
      cancelEdit();
    } catch (err) {
      setActionError(err.message);
    }
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await removeNote(note.id);
    } catch (err) {
      setActionError(err.message);
    }
  };

  const hasNotes = notes && notes.length > 0;
  const showEmpty = !loading && !hasNotes;

  return (
    <div className={`truck-notes-thread truck-notes-thread--${theme} ${className}`}>
      {error && <div className="truck-notes-error">{error}</div>}
      {actionError && <div className="truck-notes-error">{actionError}</div>}

      {loading && <div className="truck-notes-loading">Loading notes…</div>}

      {showEmpty && (
        <div className="truck-notes-empty">
          {emptyMessage || 'No notes yet.'}
        </div>
      )}

      {hasNotes && (
        <ul className="truck-notes-list">
          {notes.map((note) => {
            const ownedByMe = currentUser?.id === note.created_by;
            const canModify = canEdit && (ownedByMe || isAdmin);
            const isEditing = editingId === note.id;
            return (
              <li key={note.id} className="truck-notes-item">
                <div className="truck-notes-item-header">
                  <span className="truck-notes-author">
                    {note.author_name || note.author_username || `User #${note.created_by}`}
                  </span>
                  <span className="truck-notes-meta">
                    <span className={`truck-notes-tag truck-notes-tag-${note.note_type}`}>
                      {NOTE_TYPE_LABEL[note.note_type] || note.note_type}
                    </span>
                    <span
                      className="truck-notes-time"
                      title={note.created_at}
                    >
                      {formatTimestamp(note.created_at)}
                    </span>
                  </span>
                </div>

                {isEditing ? (
                  <div className="truck-notes-edit">
                    <textarea
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      rows={3}
                      autoFocus
                    />
                    <div className="truck-notes-edit-actions">
                      <button type="button" onClick={() => saveEdit(note)}>Save</button>
                      <button type="button" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div className="truck-notes-content">{note.content}</div>
                )}

                {note.edited_at && !isEditing && (
                  <div className="truck-notes-edited">
                    edited
                    {note.edited_by_admin ? ' by Admin' : ''}
                    {note.editor_name ? ` — ${note.editor_name}` : ''}
                    {' at '}
                    {formatTimestamp(note.edited_at)}
                  </div>
                )}

                {canModify && !isEditing && (
                  <div className="truck-notes-actions">
                    <button
                      type="button"
                      className="truck-notes-action-btn"
                      onClick={() => startEdit(note)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="truck-notes-action-btn truck-notes-action-danger"
                      onClick={() => handleDelete(note)}
                    >
                      Delete
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canEdit && (
        <form className="truck-notes-composer" onSubmit={handleAdd}>
          <textarea
            value={composer}
            onChange={(e) => setComposer(e.target.value)}
            placeholder="Add a note…"
            rows={2}
            disabled={submitting}
          />
          <div className="truck-notes-composer-actions">
            <button type="submit" disabled={submitting || !composer.trim()}>
              {submitting ? 'Adding…' : 'Add note'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
